import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal} from '@angular/core';
import {UserService} from "../../services/user.service";
import {FormsModule} from "@angular/forms";
import {CircuitBreakerStatusDto, UserRegistrationDto, UserSyncQueueStateDto, UserSyncQueueType} from "admin-api-dto";
import {CommonModule, DatePipe} from "@angular/common";
import {MessageService, PrimeTemplate} from "primeng/api";
import {TableModule} from "primeng/table";
import {Tag} from "primeng/tag";
import {ActivatedRoute, Router} from "@angular/router";
import {TypedTableRowDirective} from "../../typed-table-row.directive";
import {NewWindowLinkComponent} from "../../components/new-window-link/new-window-link.component";
import {InputText} from "primeng/inputtext";
import {Button, ButtonModule} from "primeng/button";
import {Select} from "primeng/select";
import {nycProfileStatusToTag, lastSyncStatusToTag, loginStatusToTag} from "../../enum-to-tag";
import {UserSyncService} from "../../services/user-sync.service";
import {ProgressSpinner} from "primeng/progressspinner";
import {finalize, Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, switchMap} from "rxjs/operators";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

const QUEUE_LABELS: Record<UserSyncQueueType, string> = {
  scheduled: "Scheduled queue",
  priority: "Priority queue",
};

type FilterOption = { label: string; value: string };

const filterOptions: FilterOption[] = [
  {label: 'All', value: 'all'},
  {label: 'To register', value: 'to-register'},
  {label: 'Sync failed', value: 'sync-failed'},
];

@Component({
  selector: 'app-registration-page',
  imports: [
    FormsModule,
    DatePipe,
    CommonModule,
    PrimeTemplate,
    TableModule,
    Tag,
    TypedTableRowDirective,
    NewWindowLinkComponent,
    InputText,
    Button,
    ButtonModule,
    Select,
    ProgressSpinner,
  ],
  templateUrl: './registration-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegistrationPageComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly userSyncService = inject(UserSyncService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly users = signal<UserRegistrationDto[]>([]);
  protected readonly totalCount = signal(0);
  protected readonly selectedUsers = signal<UserRegistrationDto[]>([]);
  protected readonly search = signal('');
  protected readonly filter = input('to-register');
  protected readonly selectedFilter = computed(() => this.filter() ?? 'to-register');
  protected readonly filterOptions = filterOptions;

  protected readonly nycProfileStatusToTag = nycProfileStatusToTag;
  protected readonly lastSyncStatusToTag = lastSyncStatusToTag;
  protected readonly loginStatusToTag = loginStatusToTag;

  protected readonly queueStates = signal<UserSyncQueueStateDto[]>([]);
  protected readonly queueLoading = signal(true);
  protected readonly togglingQueue = signal<UserSyncQueueType | null>(null);
  protected readonly queueLabels = QUEUE_LABELS;

  protected readonly circuitBreakerStatus = signal<CircuitBreakerStatusDto | null>(null);
  protected readonly circuitBreakerLoading = signal(true);
  protected readonly runningAutoCreate = signal(false);

  private readonly searchTrigger$ = new Subject<{ search: string; view: string }>();

  constructor() {
    this.searchTrigger$.pipe(
      debounceTime(300),
      distinctUntilChanged((a, b) => a.search === b.search && a.view === b.view),
      switchMap(params => this.userService.getRegistrationUsers(params)),
      takeUntilDestroyed(),
    ).subscribe(result => {
      this.users.set(result.users);
      this.totalCount.set(result.totalCount);
    });

    this.reloadQueues();
    this.loadCircuitBreakerStatus();
  }

  ngOnInit() {
    this.triggerSearch(this.search(), this.selectedFilter());
  }

  private triggerSearch(search: string, view: string) {
    this.searchTrigger$.next({search, view});
  }

  protected onSearchChange(value: string) {
    this.search.set(value);
    this.triggerSearch(value, this.selectedFilter());
  }

  protected readonly sortedUsers = computed(() => this.users());

  protected onFilterChange(value: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {filter: value},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    this.triggerSearch(this.search(), value);
  }

  protected copyToApply() {
    const users = this.selectedUsers().map(user => ({
      username: user.nycUsername,
      password: user.nycPassword,
      firstName: user.firstName,
      lastName: user.lastName,
      email1: user.nycMail1,
      email2: user.nycMail2,
    }));

    document.dispatchEvent(new CustomEvent("registerUsers", {detail: users}));
  }

  protected labelFor(queue: UserSyncQueueType): string {
    return QUEUE_LABELS[queue];
  }

  protected reloadQueues() {
    this.queueLoading.set(true);
    this.userSyncService.getQueues().pipe(
      finalize(() => this.queueLoading.set(false))
    ).subscribe({
      next: queues => {
        this.queueStates.set([queues.scheduled, queues.priority]);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to refresh user sync queues'
        });
      }
    });
  }

  protected toggleListener(state: UserSyncQueueStateDto) {
    const target = state.queue;
    const enable = !state.listenerEnabled;
    this.togglingQueue.set(target);
    this.userSyncService.toggleListener(target, enable).pipe(
      finalize(() => this.togglingQueue.set(null))
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: `Listener ${enable ? 'resumed' : 'paused'}`,
          detail: QUEUE_LABELS[target]
        });
        this.reloadQueues();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: `Failed to ${enable ? 'resume' : 'pause'} listener`,
          detail: QUEUE_LABELS[target]
        });
      }
    });
  }

  protected isToggling(queue: UserSyncQueueType): boolean {
    return this.togglingQueue() === queue;
  }

  protected loadCircuitBreakerStatus() {
    this.circuitBreakerLoading.set(true);
    this.userSyncService.getCircuitBreakerStatus().pipe(
      finalize(() => this.circuitBreakerLoading.set(false))
    ).subscribe({
      next: status => this.circuitBreakerStatus.set(status),
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to load circuit breaker status'
        });
      }
    });
  }

  protected runAutoCreate() {
    const userIds = this.selectedUsers().map(u => u.id);
    if (userIds.length === 0) return;

    this.runningAutoCreate.set(true);
    this.userService.runAutoCreate(userIds).pipe(
      finalize(() => this.runningAutoCreate.set(false))
    ).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: `Enqueued ${userIds.length} user(s) for auto-create`
        });
        this.selectedUsers.set([]);
        this.reloadRegistrationData();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed to run auto-create'
        });
        this.reloadRegistrationData();
      }
    });
  }

  private reloadRegistrationData() {
    this.userService.getRegistrationUsers({
      view: this.selectedFilter(),
      search: this.search() || undefined,
    }).subscribe(result => {
      this.users.set(result.users);
      this.totalCount.set(result.totalCount);
    });
  }

  protected autoCreateStatusTag(user: UserRegistrationDto): { value: string; severity: 'success' | 'danger' | 'warn' | 'secondary' } {
    switch (user.autoCreateStatus) {
      case 'in-progress':
        return {value: 'In Progress', severity: 'warn'};
      case 'failed':
        return {value: 'Failed', severity: 'danger'};
      case 'pending':
        return {value: 'Pending', severity: 'secondary'};
    }
  }
}
